import { ChangeDetectionStrategy, Component, forwardRef, inject, input, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DefaultControlValueAccessor, TinyHelpers } from '@coin/shared/core/util-helpers';
import { V2ButtonComponent } from '../button/v2-button.component';
import { LightweightRuleComponent } from './lightweight-rule/lightweight-rule.component';
import { LightweightRuleSetComponent } from './lightweight-rule-set/lightweight-rule-set.component';
import { ConditionPickerComponent } from './condition-picker/condition-picker.component';
import { emptyRule, emptyRuleEngine, emptyRuleSet, isRuleSet } from './util/functions';
import {
  LightWeightCondition,
  LightweightRule,
  LightweightRuleEngine,
  LightweightRuleEngineConfig,
  LightweightRuleSet,
  TRANSLATE_PREFIX
} from './util/types-constants';
import { asapScheduler, combineLatest, debounceTime, Observable, of, shareReplay } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'coin-v2-rule-engine-light',
  imports: [
    CommonModule,
    MatIconModule,
    V2ButtonComponent,
    LightweightRuleComponent,
    LightweightRuleSetComponent,
    MatMenuModule,
    ConditionPickerComponent,
    TranslateModule
  ],
  templateUrl: './v2-lightweight-rule-engine.component.html',
  styleUrls: ['./v2-lightweight-rule-engine.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => V2LightweightRuleEngineComponent),
      multi: true
    }
  ]
})
export class V2LightweightRuleEngineComponent extends DefaultControlValueAccessor<LightweightRuleEngine> {
  config = input.required<LightweightRuleEngineConfig>();
  /** * `true`: translates fields via `ngx-translate`
   * * `'auto'`: translates fields via `pascalCaseToText`  */
  translateFields = input<boolean | 'auto'>(false);
  allowRuleSets = input<'none' | 'flat' | 'nested'>('flat');
  additionalFooterButtons = input<TemplateRef<unknown>>();

  protected translate = inject(TranslateService);

  public searchTranslatedFields$ = combineLatest([toObservable(this.config), toObservable(this.translateFields)]).pipe(
    switchMap(([config, translateKeys]) => {
      const fields = Object.keys(config ?? {});
      const translations$ = fields.reduce<Record<string, Observable<string>>>((translationMap, field) => {
        switch (translateKeys) {
          case true:
            translationMap[field] = this.translate.stream(`${TRANSLATE_PREFIX}${field}`);
            break;
          case 'auto':
            translationMap[field] = of(TinyHelpers.pascalcaseToText(field));
            break;
          default:
            translationMap[field] = of(field);
        }

        return translationMap;
      }, {});
      return combineLatest(translations$).pipe(
        debounceTime(0, asapScheduler),
        map(translations => {
          const translatedFields = fields
            .map(field => ({ key: field, translation: translations[field] }))
            .sort((a, b) => a.translation.localeCompare(b.translation));
          return (search: string) => {
            const lowercaseSearch = search.toLowerCase();
            return of(translatedFields.filter(field => field.translation.toLowerCase().includes(lowercaseSearch)));
          };
        })
      );
    }),
    shareReplay({ refCount: true, bufferSize: 0 })
  );

  get value(): LightweightRuleEngine {
    this._value ||= emptyRuleEngine();
    return this._value;
  }

  set value(value: LightweightRuleEngine) {
    this._value = value;
    this.onChange(value);
  }

  public addRule(): void {
    this.value = { ...this.value, rules: [...this.value.rules, emptyRule()] };
  }

  public addRuleSet(): void {
    this.value = {
      ...this.value,
      rules: [...this.value.rules, emptyRuleSet({ prefillRules: this.allowRuleSets() !== 'nested' })]
    };
  }

  public deleteItem(ruleOrRuleSet: LightweightRule | LightweightRuleSet) {
    this.value = { ...this.value, rules: this.value.rules.filter(item => item !== ruleOrRuleSet) };
  }

  public registerChanges() {
    this.value = { ...this.value };
  }

  public setCondition(condition: LightWeightCondition) {
    this.value = { ...this.value, condition };
  }

  protected readonly isRuleSet = isRuleSet;
}
